import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "../api";
import { useCart } from "../context/CartContext";
import { toast } from "sonner";
import { Minus, Plus } from "lucide-react";

export default function ProductDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [product, setProduct] = useState(null);
  const [selectedImg, setSelectedImg] = useState(0);
  const [size, setSize] = useState(null);
  const [color, setColor] = useState(null);
  const [qty, setQty] = useState(1);
  const { addItem } = useCart();

  useEffect(() => {
    api.get(`/products/${id}`).then((r) => {
      setProduct(r.data);
      setSize(r.data.sizes?.[0] || null);
      setColor(r.data.colors?.[0] || null);
    });
  }, [id]);

  if (!product) return <div className="pt-32 text-center text-muted-foreground">Loading…</div>;

  const handleAdd = () => {
    if (product.sizes?.length > 0 && !size) {
      toast.error("Please select a size");
      return;
    }
    if (product.stock <= 0) {
      toast.error("Out of stock");
      return;
    }
    addItem(product, size, color, qty);
    toast.success("Added to cart");
  };

  const handleBuyNow = () => {
    handleAdd();
    nav("/cart");
  };

  return (
    <div className="pt-24 pb-24" data-testid="product-detail-page">
      <div className="mx-auto max-w-[1600px] px-6 md:px-12 grid grid-cols-1 lg:grid-cols-2 gap-12">
        {/* Images */}
        <div>
          <div className="aspect-[3/4] bg-muted/40 overflow-hidden">
            <img
              src={product.images?.[selectedImg] || product.images?.[0]}
              alt={product.name}
              className="w-full h-full object-cover"
              data-testid="product-main-image"
            />
          </div>
          {product.images?.length > 1 && (
            <div className="mt-4 grid grid-cols-5 gap-3">
              {product.images.map((img, i) => (
                <button
                  key={i}
                  onClick={() => setSelectedImg(i)}
                  className={`aspect-[3/4] overflow-hidden border ${selectedImg === i ? "border-white" : "border-white/10"}`}
                  data-testid={`product-thumb-${i}`}
                >
                  <img src={img} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Info */}
        <div className="lg:sticky lg:top-28 self-start max-w-lg">
          <div className="overline text-muted-foreground">{product.category}</div>
          <h1 className="serif text-4xl md:text-5xl mt-3 font-light">{product.name}</h1>
          <div className="text-xl font-light mt-6">${product.price.toFixed(2)}</div>

          <p className="mt-8 text-sm leading-[1.8] text-muted-foreground" data-testid="product-description">
            {product.description}
          </p>

          {product.sizes?.length > 0 && (
            <div className="mt-10">
              <div className="overline text-muted-foreground mb-3">Size</div>
              <div className="flex flex-wrap gap-2" data-testid="size-selector">
                {product.sizes.map((s) => (
                  <button
                    key={s}
                    onClick={() => setSize(s)}
                    className={`px-5 py-2.5 border text-xs uppercase tracking-[0.18em] transition ${
                      size === s
                        ? "bg-white text-black border-white"
                        : "border-white/20 hover:border-white"
                    }`}
                    data-testid={`size-${s}`}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>
          )}

          {product.colors?.length > 0 && (
            <div className="mt-6">
              <div className="overline text-muted-foreground mb-3">Colour — <span className="text-foreground">{color}</span></div>
              <div className="flex flex-wrap gap-2" data-testid="color-selector">
                {product.colors.map((c) => (
                  <button
                    key={c}
                    onClick={() => setColor(c)}
                    className={`px-4 py-2 border text-xs uppercase tracking-[0.18em] ${
                      color === c ? "border-white" : "border-white/20"
                    }`}
                    data-testid={`color-${c}`}
                  >
                    {c}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="mt-8 flex items-center gap-4">
            <div className="flex items-center border border-white/20">
              <button onClick={() => setQty(Math.max(1, qty - 1))} className="px-3 py-2" data-testid="qty-minus"><Minus size={14} /></button>
              <span className="px-4 text-sm" data-testid="qty-value">{qty}</span>
              <button onClick={() => setQty(qty + 1)} className="px-3 py-2" data-testid="qty-plus"><Plus size={14} /></button>
            </div>
            <div className="text-xs text-muted-foreground">
              {product.stock > 0 ? `${product.stock} in stock` : "Out of stock"}
            </div>
          </div>

          <div className="mt-8 flex flex-col sm:flex-row gap-3">
            <button className="btn-sharp primary flex-1" onClick={handleAdd} data-testid="add-to-cart-btn">
              Add to Cart
            </button>
            <button className="btn-sharp ghost flex-1" onClick={handleBuyNow} data-testid="buy-now-btn">
              Buy Now
            </button>
          </div>

          <div className="mt-10 border-t border-white/10 pt-8 space-y-2 text-xs uppercase tracking-[0.18em] text-muted-foreground">
            <div>Complimentary shipping worldwide</div>
            <div>30-day returns</div>
            <div>Authentication guaranteed</div>
          </div>
        </div>
      </div>
    </div>
  );
}
